import React from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';

const PrivacyPolicy = () => {
    return (
        <div className="min-h-screen bg-background text-text-primary">
            <Helmet>
                <title>Privacy Policy - Northbridge Valuation & Advisory</title>
                <meta name="description" content="Privacy Policy for Northbridge Valuation & Advisory. Learn how we protect your personal and business information." />
            </Helmet>
            <Header />

            <main className="max-w-4xl mx-auto px-6 py-20">
                <h1 className="text-4xl lg:text-5xl font-bold text-primary mb-4">Privacy Policy</h1>
                <p className="text-text-secondary mb-12">Effective Date: January 1, 2026</p>

                <div className="space-y-10 prose prose-slate max-w-none">
                    <section>
                        <h2 className="text-2xl font-bold text-primary mb-4">1. Introduction</h2>
                        <p className="text-text-secondary leading-relaxed">
                            Northbridge Valuation & Advisory ("we," "us," or "our") is committed to protecting the privacy and confidentiality of the personal and financial information entrusted to us. This Privacy Policy outlines how we collect, use, disclose, and safeguard your information when you visit our website (northbridgevaluation.com) or engage our professional services.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-2xl font-bold text-primary mb-4">2. Information We Collect</h2>
                        <ul className="list-disc pl-5 space-y-3 text-text-secondary">
                            <li><strong>Personal Identification Information:</strong> Name, email address, phone number, and mailing address provided via contact forms or engagement agreements.</li>
                            <li><strong>Financial & Business Data:</strong> Tax returns, financial statements, organizational charts, and legal documents provided for the purpose of performing business valuations or litigation support.</li>
                            <li><strong>Technical Data:</strong> IP addresses, browser types, and cookies used to improve website functionality and user experience.</li>
                        </ul>
                    </section>

                    <section>
                        <h2 className="text-2xl font-bold text-primary mb-4">3. How We Use Your Information</h2>
                        <p className="text-text-secondary mb-4 italic leading-relaxed">We use the collected information to:</p>
                        <ul className="list-disc pl-5 space-y-3 text-text-secondary">
                            <li>Perform requested valuation and advisory services.</li>
                            <li>Communicate regarding project status, billing, and inquiries.</li>
                            <li>Comply with legal, regulatory, or professional standards (e.g., USPAP, IRS, or court mandates).</li>
                            <li>Maintain and improve our website performance.</li>
                        </ul>
                    </section>

                    <section>
                        <h2 className="text-2xl font-bold text-primary mb-4">4. Confidentiality & Data Security</h2>
                        <ul className="list-disc pl-5 space-y-3 text-text-secondary">
                            <li>Confidentiality is a cornerstone of our practice.</li>
                            <li>We implement industry-standard physical, electronic, and procedural safeguards to protect your data.</li>
                            <li>Access to sensitive financial information is restricted to authorized personnel directly involved in your engagement.</li>
                            <li>We do not sell, rent, or trade your personal or business information to third parties.</li>
                        </ul>
                    </section>

                    <section>
                        <h2 className="text-2xl font-bold text-primary mb-4">5. Third-Party Disclosure</h2>
                        <p className="text-text-secondary mb-4 italic leading-relaxed">We may share information with third parties only in the following circumstances:</p>
                        <ul className="list-disc pl-5 space-y-3 text-text-secondary">
                            <li><strong>Service Providers:</strong> Subcontractors or specialized data providers (e.g., market database services) who are bound by strict confidentiality agreements.</li>
                            <li><strong>Legal Necessity:</strong> To comply with a subpoena, court order, or legal process.</li>
                            <li><strong>Authorized Representatives:</strong> To your attorney or CPA, provided you have given us explicit authorization to do so.</li>
                        </ul>
                    </section>

                    <section>
                        <h2 className="text-2xl font-bold text-primary mb-4">6. Cookies & Tracking</h2>
                        <p className="text-text-secondary leading-relaxed">
                            Our website may use cookies to enhance navigation. You can choose to set your web browser to refuse cookies, though some parts of the site may not function properly as a result.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-2xl font-bold text-primary mb-4">7. Changes to This Policy</h2>
                        <p className="text-text-secondary leading-relaxed">
                            Northbridge Valuation & Advisory reserves the right to update this policy at any time. We encourage users to check this page periodically for any changes.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-2xl font-bold text-primary mb-4">8. Contact Us</h2>
                        <p className="text-text-secondary leading-relaxed">
                            If you have questions about this Privacy Policy, please contact us at:<br />
                            <strong>Email:</strong> <a href="mailto:info@northbridgevaluation.com" className="text-accent hover:underline">info@northbridgevaluation.com</a><br />
                            <strong>Website:</strong> <a href="https://northbridgevaluation.com" className="text-accent hover:underline">northbridgevaluation.com</a>
                        </p>
                    </section>
                </div>
            </main>

            <Footer />
        </div>
    );
};

export default PrivacyPolicy;
